/**
 * Mashg'ulot dvigateli (demo `L` + startLesson/startPractice/startGame/startCards/startMatch/mark/next/prev/finish).
 * Holat Zustand store'da; ekran (`Lesson.tsx`) joriy qadamni ko'rsatadi, javobni `mark()` bilan yozadi, `next()` bilan o'tadi.
 */
import { create } from 'zustand';
import { BYID, INTERVALS, W, shuffle } from '../../data';
import type { Word } from '../../data/types';
import { S, lessonSource, logDay, newToday, started, today } from '../../store';
import { nav } from '../../lib/nav';
import { toast } from '../../lib/toast';
import { primeKeyboard } from '../../lib/keyboard';
import { prefetchAudio } from '../../lib/speech';
import { t as tr } from '../../i18n';
import { LN } from '../../data';
import { applyCourse, fire, sync } from '../../lib/sync';
import { XPR, estXp } from '../../lib/xp';

export type StepType = 'intro' | 'card' | 'flip' | 'rec' | 'recUz' | 'listen' | 'write' | 'speak' | 'gap' | 'match';
export type LessonMode = 'lesson' | 'practice' | 'test' | 'cards' | 'match';

export interface Step {
  t: StepType;
  w?: Word;               // match'dan tashqari hamma qadamda bor
  stage?: number;         // dars bosqichi 0..4 (faqat mode='lesson')
  retry?: boolean;        // xato so'zning qaytarilgan nusxasi
  listen?: boolean;       // write: eshitib yozish
  note?: string;          // listen: mikrofon o'rniga eshitib tanlash izohi
  ws?: Word[];            // match
  timed?: boolean;        // match
  count?: number;         // intro
}

/** Dars 5 bosqichda. */
export const STAGES = [
  { k: 'card', n: 'Oʻrganish', d: 'Har bir soʻzni koʻring, tinglang va misolni oʻqing. Strelkalar bilan oldinga-orqaga yuring.' },
  { k: 'rec', n: 'Tarjimani toping', d: LN.Adj + ' soʻzning toʻgʻri oʻzbekcha tarjimasini tanlang.' },
  { k: 'write', n: 'Yozing', d: 'Oʻzbekcha soʻzni ' + LN.adj + ' yozing. Kataklar soni — harflar soni.' },
  { k: 'recUz', n: 'Variantlar bilan tekshirish', d: 'Oʻzbekcha soʻzning ' + LN.adj + 'sini variantlardan toping.' },
  { k: 'speak', n: 'Talaffuz', d: 'Namunani tinglang va soʻzni ovoz chiqarib ayting.' },
] as const;

export interface Feedback {
  ok: boolean;
  w: Word;
  ans?: React.ReactNode;   // to'g'ri javob ko'rinishi (diff, imlo, gap)
  head?: string;
  msg?: React.ReactNode;
}

export interface LessonState {
  mode: LessonMode;
  title?: string;
  steps: Step[];
  i: number;
  res: Record<number, boolean>;   // so'z bo'yicha yakuniy natija (bir marta xato — false)
  nw: number[];                   // yangi so'zlar (dars)
  known: number[];                // «Bilaman» belgilanganlar
  ok: number; tot: number; xp: number;
  last: number;                   // oxirgi to'g'ri javob XP'si (pastki varaqda)
  retried: Record<string, 1>;
  wrongIds: number[];
  /** Darsda so'z bo'yicha xatolar soni (WRONG_LIMIT dan keyin so'z darsdan chiqariladi). */
  wrongN: Record<number, number>;
  built: boolean;                 // dars: 2–5 bosqichlar yig'ildimi
  noteShown?: boolean;
  feedback: Feedback | null;      // pastki varaq
  match?: { secs: number; mistakes: number; timed: boolean }; // Juftlik yakuni (serverga)
  /** Yakun uchun hisoblangan sonlar (finish() to'ldiradi). */
  summary?: { nN: number; acc: number; goalDone: boolean; words: Word[]; wrong: Word[]; title: string; xp: number };
}

interface LessonStore {
  L: LessonState | null;
  set: (patch: Partial<LessonState> | ((l: LessonState) => Partial<LessonState>)) => void;
  reset: () => void;
}
export const useLesson = create<LessonStore>((set, get) => ({
  L: null,
  set: (patch) => { const L = get().L; if (!L) return; set({ L: { ...L, ...(typeof patch === 'function' ? patch(L) : patch) } }); },
  reset: () => set({ L: null }),
}));
const Lget = () => useLesson.getState().L!;
const Lset = (p: Partial<LessonState> | ((l: LessonState) => Partial<LessonState>)) => useLesson.getState().set(p);

const blank = (o: Partial<LessonState>): LessonState => ({
  mode: 'practice', steps: [], i: 0, res: {}, nw: [], known: [], ok: 0, tot: 0, xp: 0, last: 0, retried: {}, wrongIds: [], wrongN: {}, built: true, feedback: null, ...o,
});
/** Kunlik maqsad bonusi berilgan kun (taxmin uchun; server `goal_day` bilan tekshiradi). */
let goalDay: number | null = null;
/** Serverdagi sessiya id'si (yaratilish tugaguncha promise; API bo'lmasa null). */
let sid: Promise<number | null> = Promise.resolve(null);
function begin(L: LessonState) {
  useLesson.setState({ L }); nav.push({ k: 'lesson' });
  const wordIds = [...new Set(L.steps.flatMap((s) => (s.w ? [s.w.id] : (s.ws ?? []).map((w) => w.id))))];
  const body = L.mode === 'lesson' ? { type: 'lesson', new_ids: L.nw } : { type: L.mode, title: L.title, word_ids: wordIds };
  sid = sync.session(body).then((r) => r.id).catch((e) => { console.warn('[session]', e instanceof Error ? e.message : e); return null; });
}
export function setMatch(m: NonNullable<LessonState['match']>) { Lset({ match: m }); }

/* ---------- boshlash ---------- */
/** Darsda bitta so'zga shuncha xatodan keyin so'z qayta qo'yilmaydi. */
export const WRONG_LIMIT = 5;
export const GOAL_MIN = 3, GOAL_MAX = 10, GOAL_DEFAULT = 5;
export const clampGoal = (n: number) => (n > 0 ? Math.min(GOAL_MAX, Math.max(GOAL_MIN, n)) : GOAL_DEFAULT);
/** Bugun darsda yana nechta yangi so'z olinadi: kunlik maqsad − bugun o'rganilganlar. */
export const lessonNewLeft = () => Math.max(0, clampGoal(S().goal) - newToday());
/** Kuniga ko'pi bilan shuncha yangi so'z (maqsaddan keyingi «Yana o'rganish» darslari bilan birga) — takrorlash yo'q, ortig'i eslab qolinmaydi. */
export const DAILY_MAX = 50;
/** Maqsad bajarilgach «Yana o'rganish» darsi: kunlik maqsad hajmida, DAILY_MAX va qolgan yangi so'zlar bilan cheklanadi. */
export const extraNewLeft = () => Math.max(0, Math.min(clampGoal(S().goal), DAILY_MAX - newToday()));
/** `extra` — maqsad bajarilgandan keyingi qo'shimcha dars. */
export function startLesson(extra = false) {
  const t = today(); const s = S();
  if (s.newDay !== t) s.set({ newDay: t, newCount: 0 });
  // Takrorlash yo'q: dars = kunlik maqsadning bugun qolgan qismi (lexo-api core.LessonNew bilan bir xil).
  // Maqsad bajarilsa — ertangacha dars yo'q.
  const src = lessonSource();
  const want = lessonNewLeft() || (extra ? extraNewLeft() : 0);
  const nw = src.slice(0, Math.max(0, Math.min(want, src.length)));
  if (!nw.length) {
    toast(tr(!src.length ? 'Yangi soʻz qolmadi' : extra ? 'Bugun yetarli oʻrgandingiz — ertaga davom etamiz' : 'Bugungi reja bajarildi — ertaga davom etamiz'));
    return;
  }
  const L = blank({ mode: 'lesson', nw: nw.map((w) => w.id), built: false });
  L.steps = nw.map((w) => ({ t: 'card', w, stage: 0 }));
  prefetchAudio(L.nw);
  begin(L);
}
function buildStagesInto(L: LessonState) {
  const words = L.nw.filter((id) => !L.known.includes(id)).map((id) => BYID[id]!);
  L.built = true;
  if (!words.length) return;
  for (const si of [1, 2, 3, 4]) shuffle(words).forEach((w) => L.steps.push({ t: STAGES[si]!.k, w, stage: si }));
}
export function startPractice(ws: Word[], title: string, isTest = false) {
  if (!ws.length) return;
  const steps: Step[] = [];
  ws.forEach((w) => steps.push({ t: isTest ? (Math.random() < 0.5 ? 'rec' : 'recUz') : 'rec', w }));
  if (!isTest) ws.forEach((w) => steps.push({ t: 'write', w }));
  begin(blank({ mode: isTest ? 'test' : 'practice', title, steps }));
}
export function startGame(steps: Step[], title: string) { if (!steps.length) return; begin(blank({ mode: 'practice', title, steps })); }
export function startCards(ws: Word[]) { begin(blank({ mode: 'cards', steps: ws.map((w) => ({ t: 'flip', w })) })); }
export function startMatch(ws: Word[]) {
  const pool = ws && ws.length >= 3 ? ws : (started().length >= 6 ? started() : W);
  begin(blank({ mode: 'match', steps: [{ t: 'match', ws: shuffle(pool).slice(0, 6), timed: true }] }));
}

/* ---------- oqim ---------- */
export function lessonTotal(L: LessonState) {
  if (L.mode !== 'lesson' || L.built) return L.steps.length;
  const learn = L.nw.length - L.known.length;
  return L.steps.length + (learn ? 4 * learn : 0);
}

/** Javobni yozish. Xato bo'lsa (test'dan tashqari) so'z shu bosqich oxirida qaytadi. */
export function mark(w: Word, ok: boolean, retry = true) {
  const L = { ...Lget() };
  L.tot++; L.last = 0;
  if (ok) {
    L.ok++;
    L.last = L.mode === 'lesson' ? XPR.lessonAns : XPR.gameAns;
    L.xp += L.last;
  } else if (!L.wrongIds.includes(w.id)) L.wrongIds = [...L.wrongIds, w.id];
  L.res = { ...L.res, [w.id]: (L.res[w.id] ?? true) && ok };
  const s = S(); s.set({ right: s.right + (ok ? 1 : 0), wrong: s.wrong + (ok ? 0 : 1) });
  const ws = s.w[w.id]; if (ws && !ok) s.set({ w: { ...s.w, [w.id]: { ...ws, wr: (ws.wr || 0) + 1 } } });
  if (!ok) L.wrongN = { ...L.wrongN, [w.id]: (L.wrongN[w.id] ?? 0) + 1 };
  if (!ok && L.mode === 'lesson' && L.wrongN[w.id]! >= WRONG_LIMIT) {
    // Cheksiz qaytarmaslik: so'zning qolgan qadamlari olib tashlanadi, so'z qiyinlarga qo'shiladi va
    // «Qiyin so'zlar» bo'limida mashq qilinadi.
    L.steps = L.steps.filter((x, j) => j <= L.i || x.w?.id !== w.id);
    if (!(S().hm ?? []).includes(w.id)) S().toggleHard(w.id);
    toast(tr('Bu soʻz keyingi darslarda qayta oʻrganiladi'));
  } else if (!ok && retry && L.mode !== 'test') {
    const cur = L.steps[L.i]!, key = w.id + ':' + (cur.stage ?? cur.t);
    if (L.mode === 'lesson' || !L.retried[key]) {
      L.retried = { ...L.retried, [key]: 1 };
      let end = L.i;
      while (end + 1 < L.steps.length && L.steps[end + 1]!.stage === cur.stage && L.steps[end + 1]!.t !== 'intro') end++;
      L.steps = [...L.steps]; L.steps.splice(end + 1, 0, { ...cur, retry: true });
    }
  }
  useLesson.setState({ L });
  const cur = L.steps[L.i];
  const client_id = `${w.id}:${cur?.stage ?? cur?.t ?? 'x'}:${L.tot}`;
  fire(sid.then((id) => (id ? sync.answer(id, { client_id, word_id: w.id, step: cur?.t ?? 'rec', ok }) : null)), 'answer');
}
export const showFeedback = (f: Feedback | null) => Lset({ feedback: f });

export function next() {
  const L = { ...Lget(), feedback: null };
  const before = L.steps[L.i]; L.i++;
  if (L.mode === 'lesson' && !L.built && L.i >= L.steps.length) { L.steps = [...L.steps]; buildStagesInto(L); }
  if (L.i >= L.steps.length) { useLesson.setState({ L }); return finish(); }
  const now = L.steps[L.i]!;
  if (now.t === 'write') primeKeyboard();
  if (L.mode === 'lesson' && before && now.stage != null && now.stage !== before.stage) toast(`${now.stage + 1}-bosqich: ${STAGES[now.stage]!.n}`);
  useLesson.setState({ L });
}
export function prev() { const L = Lget(); if (L.i > 0 && L.steps[L.i - 1]!.t === 'card') Lset({ i: L.i - 1 }); }
export function replaceStep(patch: Partial<Step>) { const L = Lget(); const steps = [...L.steps]; steps[L.i] = { ...steps[L.i]!, ...patch }; Lset({ steps }); }
export function pushStep(s: Step) { Lset((L) => ({ steps: [...L.steps, s] })); }
export function toggleKnown(id: number) { Lset((L) => ({ known: L.known.includes(id) ? L.known.filter((x) => x !== id) : [...L.known, id] })); }
export function addXp(n: number) { Lset((L) => ({ xp: L.xp + n, last: 0 })); }

/** «×» bosilganda: dars boshlangan bo'lsa yakunlanadi, aks holda chiqadi. */
export function quit() { const L = Lget(); if (L.mode === 'lesson' && L.tot > 0) finish(); else { useLesson.getState().reset(); nav.pop(); } }

/* ---------- yakun ---------- */
export function finish() {
  const L = Lget(); const s = S(); const t = today();
  let nN = 0;
  const w = { ...s.w };
  let newDay = s.newDay, newCount = s.newCount;
  if (L.mode === 'lesson') {
    if (newDay !== t) { newDay = t; newCount = 0; }
    L.known.forEach((id) => { w[id] = { box: 3, due: t + INTERVALS[3], wr: 0 }; });
    Object.entries(L.res).forEach(([ids, ok]) => {
      const id = +ids; if (L.known.includes(id)) return;
      if (w[id]) return; // allaqachon o'rganilgan (takrorlash yo'q — holati o'zgarmaydi)
      w[id] = { box: 1, due: t + INTERVALS[1], wr: ok ? 0 : 1 }; nN++;
    });
    nN += L.known.length; newCount += nN;
    s.set({ w, newDay, newCount });
    logDay('n', nN);
  } else logDay('r', 0);
  // XP taxmini (demo `finish`): dars — javoblar + yangi so'z ×10 + xatosiz +15 + kunlik maqsad +40; o'yinlar — 60 gacha; kartochkalar — 0.
  // Haqiqiy qiymat (ko'paytuvchi va kunlik chegaralar bilan) server javobida keladi.
  const goalDone = L.mode === 'lesson' && newToday() >= clampGoal(S().goal);
  let base = L.xp;
  if (L.mode === 'lesson') {
    base += nN * XPR.newWord;
    if (L.tot && L.ok === L.tot) base += XPR.allRight;
    if (goalDone && goalDay !== t) { goalDay = t; base += XPR.goal; }
  } else if (L.mode !== 'cards') base = Math.min(base, XPR.gameCap);
  const xp = L.mode === 'cards' ? 0 : estXp(base);
  const s2 = S();
  let streak = s2.streak;
  if (s2.lastDay === null || t - s2.lastDay > 1) streak = 1; else if (t - s2.lastDay === 1) streak++;
  s2.set({ xp: s2.xp + xp, streak, lastDay: t, bestStreak: Math.max(s2.bestStreak, streak) });
  const acc = L.tot ? Math.round(L.ok / L.tot * 100) : 100;
  const words = [...new Set(L.steps.filter((x) => x.w).map((x) => x.w!))].slice(0, 6);
  const title = L.mode === 'test' ? `Test: ${L.ok}/${L.tot}` : goalDone ? 'Kunlik maqsad bajarildi!' : L.mode === 'lesson' ? 'Dars tugadi!' : L.title ? `${L.title}: ${L.ok}/${L.tot}` : 'Mashq tugadi!';
  const wrong = L.wrongIds.map((id) => BYID[id]!);
  Lset({ xp, feedback: null, summary: { nN, acc, goalDone, words, wrong, title, xp } });
  nav.replace({ k: 'finish' });
  // Server — haqiqat manbai: yakun javobidagi so'z qutilari va kurs holati store'ni qayta yozadi
  const known = [...L.known], match = L.match;
  fire(sid.then((id) => (id ? sync.finish(id, { known, match }).then((r) => {
    applyCourse(r.course, r.words);
    const cur = useLesson.getState().L;
    if (cur?.summary) Lset({ xp: r.xp, summary: { ...cur.summary, xp: r.xp } });
  }) : null)), 'finish');
}
